/**
 * Task Card Component
 * Displays a linked task in a message thread - WhatsApp style card
 */

import React, { useState, useEffect } from 'react';
import { ClipboardList, Calendar, MapPin, AlertCircle, CheckCircle, Clock, ExternalLink } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { Task, TaskWithDetails } from '@/services/taskService';
import { complianceService } from '@/firebase/complianceService';
import { locationService } from '@/firebase/locationService';

interface TaskCardProps {
  taskId: string;
  organizationId: string;
  onViewTask?: (task: TaskWithDetails) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ taskId, organizationId, onViewTask }) => {
  const [task, setTask] = useState<TaskWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTask();
  }, [taskId, organizationId]);

  const loadTask = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch task document
      const taskRef = doc(db, 'tasks', taskId);
      const taskSnap = await getDoc(taskRef);

      if (!taskSnap.exists()) {
        setError('Task not found');
        setLoading(false);
        return;
      }

      const taskData = { id: taskSnap.id, ...taskSnap.data() } as Task;

      // Validate task data
      if (!taskData || !taskData.locationId) {
        setError('Invalid task data');
        setLoading(false);
        return;
      }

      // Fetch template and location for enrichment
      const templatesResult = await complianceService.getAllTemplates(organizationId);
      const locationsResult = await locationService.getAllLocations();

      if (!templatesResult.success || !locationsResult.success) {
        setError('Failed to load task details');
        setLoading(false);
        return;
      }

      const templates = templatesResult.data || [];
      const locations = locationsResult.data || [];

      // Find template and location - handle null/undefined templateId
      let template = null;
      if (taskData.templateId || taskData.originalTemplateId) {
        template = templates.find(t => 
          (taskData.templateId && t.id === taskData.templateId) || 
          (taskData.originalTemplateId && t.id === taskData.originalTemplateId)
        ) || null;
      }
      
      // Fall back to templateSnapshot if template not found
      if (!template && taskData.templateSnapshot) {
        template = taskData.templateSnapshot;
      }
      
      const location = locations.find(l => l.id === taskData.locationId);

      // Convert timestamps
      const convertTimestamp = (ts: any): Date => {
        if (ts?.toDate) return ts.toDate();
        if (ts?._seconds) return new Date(ts._seconds * 1000);
        if (ts instanceof Date) return ts;
        if (typeof ts === 'number') return new Date(ts);
        return new Date();
      };

      const dueDate = convertTimestamp(taskData.dueDate);
      const now = new Date();
      
      // Determine status - handle null/undefined status
      let taskStatus = taskData.status || 'pending';
      if (taskStatus === 'pending' && dueDate < now) {
        taskStatus = 'overdue';
      }

      // Build enriched task - handle null templateData
      const templateData = template || taskData.templateSnapshot;
      const enrichedTask: TaskWithDetails = {
        ...taskData,
        status: taskStatus,
        templateName: (templateData as any)?.name || 
                      (taskData.templateSnapshot as any)?.name || 
                      `Task ${taskId}`,
        locationName: location?.name || `Location ${taskData.locationId || 'Unknown'}`,
        locationState: location?.state,
        category: (templateData as any)?.category || 'Compliance',
        riskLevel: ((templateData as any)?.risk_level || 'medium') as string,
        riskWeight: (templateData as any)?.risk_weight || 0.6,
        evidenceTypes: (templateData as any)?.evidence_types || [],
        passFailCriteria: (templateData as any)?.pass_fail_criteria || '',
        jurisdiction: (templateData as any)?.jurisdiction || '',
        jurisdictionNotes: (templateData as any)?.jurisdiction_notes || '',
        ownerRole: (templateData as any)?.owner_role || 'manager',
        leadTimeDays: (templateData as any)?.lead_times_days?.[0] || 30,
        dueDate,
        createdAt: convertTimestamp(taskData.createdAt),
        updatedAt: convertTimestamp(taskData.updatedAt),
      };

      setTask(enrichedTask);
    } catch (err: any) {
      console.error('Error loading task:', err);
      setError(err.message || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending_review':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'overdue':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending_review':
        return <Clock className="w-4 h-4" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <ClipboardList className="w-4 h-4" />;
    }
  };

  const formatDate = (date: Date): string => {
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0 && diffDays < 7) return `In ${diffDays} days`;
    if (diffDays < 0 && diffDays > -7) return `${Math.abs(diffDays)} days ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-3 my-2 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 my-2">
        <p className="text-sm text-red-800">{error || 'Task not found'}</p>
      </div>
    );
  }

  // Additional safety check
  if (!task || !task.templateName) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 my-2">
        <p className="text-sm text-red-800">Task data incomplete</p>
      </div>
    );
  }

  return (
    <div 
      className="bg-white border border-gray-200 rounded-lg p-3 my-2 hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={() => onViewTask && task && onViewTask(task)}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <ClipboardList className="w-4 h-4 text-gray-600 flex-shrink-0" />
          <h4 className="text-sm font-semibold text-gray-900 truncate">
            {task.templateName || 'Task'}
          </h4>
        </div>
        {onViewTask && (
          <ExternalLink className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-2" />
        )}
      </div>

      <div className="space-y-1.5">
        {/* Status Badge */}
        <div className="flex items-center space-x-2">
          <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(task.status || 'pending')}`}>
            {getStatusIcon(task.status || 'pending')}
            <span className="capitalize">{(task.status || 'pending').replace('_', ' ')}</span>
          </span>
        </div>

        {/* Location */}
        {task.locationName && (
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{task.locationName}</span>
          </div>
        )}

        {/* Due Date */}
        {task.dueDate && (
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Due: {formatDate(task.dueDate)}</span>
          </div>
        )}

        {/* Category & Risk Level */}
        {(task.category || task.riskLevel) && (
          <div className="flex items-center space-x-3 text-xs text-gray-500 pt-1">
            {task.category && (
              <span className="truncate">{task.category}</span>
            )}
            {task.riskLevel && (
              <span className="capitalize">• {task.riskLevel} Risk</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskCard;

